import React, { useState, useEffect } from 'react';
import { FaUsers, FaUserCheck, FaStore, FaRupeeSign, FaWallet, FaChartLine } from 'react-icons/fa';
import { AdminAffiliateApiService } from '../../../service/admin/adminAffiliateApiService';
import './AffiliateDashboard.css';

export default function AffiliateDashboard() {
  const [stats, setStats] = useState({
    totalAffiliates: 0,
    activeAffiliates: 0,
    totalSellers: 0,
    totalPayout: 0,
    pendingWithdrawals: 0,
    totalGMV: 0
  });

  const [topAffiliates, setTopAffiliates] = useState([]);
  const [allAffiliates, setAllAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await AdminAffiliateApiService.getAffiliateDashboard();
      if (response && response.data) {
        setStats(response.data.stats || {
          totalAffiliates: 0,
          activeAffiliates: 0,
          totalSellers: 0,
          totalPayout: 0,
          pendingWithdrawals: 0,
          totalGMV: 0
        });
        setTopAffiliates(response.data.topAffiliates || []);
        setAllAffiliates(response.data.allAffiliates || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set default values on error
      setStats({
        totalAffiliates: 0,
        activeAffiliates: 0,
        totalSellers: 0,
        totalPayout: 0,
        pendingWithdrawals: 0,
        totalGMV: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 1000).toFixed(0)}K`;
    }
    return `₹${amount.toFixed(0)}`;
  };

  const getKpiIcon = (label) => {
    const icons = {
      'Total Affiliates': FaUsers,
      'Active Affiliates': FaUserCheck,
      'Total Sellers (via Affiliates)': FaStore,
      'Total Payout': FaRupeeSign,
      'Pending Withdrawals': FaWallet,
      'Total Affiliate GMV': FaChartLine
    };
    return icons[label] || FaUsers;
  };

  const getKpiColor = (label) => {
    const colors = {
      'Total Affiliates': '#1e40af', // Dark blue
      'Active Affiliates': '#059669', // Green
      'Total Sellers (via Affiliates)': '#2563eb', // Blue
      'Total Payout': '#059669', // Green
      'Pending Withdrawals': '#ea580c', // Orange
      'Total Affiliate GMV': '#2563eb' // Blue
    };
    return colors[label] || '#1e40af';
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return '#10b981';
      case 'pending':
        return '#f59e0b';
      case 'suspended':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="affiliate-dashboard-loading">
        <div className="affiliate-dash-loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="affiliate-dashboard">
      <div className="admin-page-header">
        <h1>Admin Dashboard</h1>
        <p>Overview of affiliate program performance and metrics</p>
      </div>

      {/* KPI Cards */}
      <div className="affiliate-dash-kpi-grid">
        <div className="kpi-card">
          <div className="affiliate-dash-kpi-icon" style={{ backgroundColor: getKpiColor('Total Affiliates') }}>
            <FaUsers />
          </div>
          <div className="kpi-content">
            <div className="affiliate-dash-kpi-value">{stats.totalAffiliates}</div>
            <div className="kpi-label">Total Affiliates</div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="affiliate-dash-kpi-icon" style={{ backgroundColor: getKpiColor('Active Affiliates') }}>
            <FaUserCheck />
          </div>
          <div className="kpi-content">
            <div className="affiliate-dash-kpi-value">{stats.activeAffiliates}</div>
            <div className="kpi-label">Active Affiliates</div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="affiliate-dash-kpi-icon" style={{ backgroundColor: getKpiColor('Total Sellers (via Affiliates)') }}>
            <FaStore />
          </div>
          <div className="kpi-content">
            <div className="affiliate-dash-kpi-value">{stats.totalSellers}</div>
            <div className="kpi-label">Total Sellers (via Affiliates)</div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="affiliate-dash-kpi-icon" style={{ backgroundColor: getKpiColor('Total Payout') }}>
            <FaRupeeSign />
          </div>
          <div className="kpi-content">
            <div className="affiliate-dash-kpi-value">{formatCurrency(stats.totalPayout)}</div>
            <div className="kpi-label">Total Payout</div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="affiliate-dash-kpi-icon" style={{ backgroundColor: getKpiColor('Pending Withdrawals') }}>
            <FaWallet />
          </div>
          <div className="kpi-content">
            <div className="affiliate-dash-kpi-value">{formatCurrency(stats.pendingWithdrawals)}</div>
            <div className="kpi-label">Pending Withdrawals</div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="affiliate-dash-kpi-icon" style={{ backgroundColor: getKpiColor('Total Affiliate GMV') }}>
            <FaChartLine />
          </div>
          <div className="kpi-content">
            <div className="affiliate-dash-kpi-value">{formatCurrency(stats.totalGMV)}</div>
            <div className="kpi-label">Total Affiliate GMV</div>
          </div>
        </div>
      </div>

      {/* All Affiliates Table */}
      <div className="affiliate-dash-table-section">
        <h2>All Affiliates</h2>
        <div className="table-container">
          <table className="affiliate-dash-affiliates-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Sellers</th>
                <th>Total Earnings</th>
                <th>Plans</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {allAffiliates.map((affiliate, index) => (
                <tr key={index}>
                  <td className="name-cell">{affiliate.name}</td>
                  <td className="affiliate-dash-sellers-cell">
                    {affiliate.sellers} ({affiliate.eligibleSellers} eligible)
                  </td>
                  <td className="earnings-cell">{formatCurrency(affiliate.totalEarnings)}</td>
                  <td className="affiliate-dash-plan-cell">{affiliate.plan}</td>
                  <td className="status-cell">
                    <span 
                      className="affiliate-dash-status-badge" 
                      style={{ backgroundColor: getStatusColor(affiliate.status) }}
                    >
                      {affiliate.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
